'use client';

import { cn } from '@/lib/utils';
import { ProductImageContainer } from '@/components/product-image';
import DynamicImageProvider, { useDynamicImageContext } from '@/providers/dynamic-image';
import { PRODUCT_CARD_HORIZONTAL_HEIGHTS, PRODUCT_CARD_HORIZONTAL_WIDTHS } from '@/lib/images/product-card-sizes';
import type { ComponentProps } from 'react';

export type ProductTileImageCustomProps = ComponentProps<typeof ProductImageContainer> & {
    /**
     * Eataly Library: square PLP (707-1688) = cover, 16px media corners;
     * horizontal = cover in fixed-width rail (fills container, no letterboxing).
     */
    variant: 'plp-vertical' | 'plp-horizontal';
};

/** Library image corners 16px: in this theme `rounded-2xl` is 40px (`--corner-40`), so use `rounded-lg` → `--corner-16`. */
const VARIANT_CLASS: Record<ProductTileImageCustomProps['variant'], string> = {
    'plp-vertical': cn(
        '!aspect-square h-full w-full !border-0 !bg-transparent',
        '[&_a]:!block [&_a]:!h-full [&_a]:!min-h-0 [&_a]:!min-w-0 [&_a]:!flex-1',
        '[&_a]:!overflow-hidden [&_a]:!rounded-lg',
        '[&_img]:!h-full [&_img]:!w-full [&_img]:!min-h-0 [&_img]:!object-cover [&_img]:!object-center [&_img]:!max-h-none [&_img]:!rounded-lg'
    ),
    'plp-horizontal': cn(
        '!aspect-auto h-full w-full min-h-0 min-w-0 max-h-44 !border-0 !bg-transparent',
        '[&_a]:!block [&_a]:!h-full [&_a]:!min-h-0 [&_a]:!w-full [&_a]:!min-w-0 [&_a]:!flex-1',
        '[&_a]:!overflow-hidden [&_a]:!rounded-lg',
        '[&_img]:!h-full [&_img]:!w-full [&_img]:!min-h-0 [&_img]:!object-cover [&_img]:!object-center [&_img]:!max-h-none [&_img]:!rounded-lg'
    ),
};

/**
 * Tile-scoped `ProductImageContainer` presets (Library PLP + horizontal card) to avoid
 * long inline `className` on `ProductTileCustom` and to match Figma radii and object fit.
 */
export function ProductTileImageCustom({ variant, className, pdpLink = false, ...props }: ProductTileImageCustomProps) {
    const parentImageContext = useDynamicImageContext();
    const image = (
        <ProductImageContainer pdpLink={pdpLink} {...props} className={cn(VARIANT_CLASS[variant], className)} />
    );

    if (variant !== 'plp-horizontal') {
        return image;
    }

    return (
        <DynamicImageProvider
            value={{
                widths: PRODUCT_CARD_HORIZONTAL_WIDTHS,
                heights: PRODUCT_CARD_HORIZONTAL_HEIGHTS,
                hasSource: parentImageContext ? (src) => parentImageContext.hasSource(src) : undefined,
                addSource: parentImageContext ? (src) => parentImageContext.addSource(src) : undefined,
            }}>
            {image}
        </DynamicImageProvider>
    );
}
